"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { showToast } from "@/components/ui/sonner";
import { logout } from "@/lib/api/generated";
import {
  handleExplicitLogout,
  type ExplicitLogoutOptions,
} from "@/lib/session/session-logout";
import type {
  LogoutRequest,
  SessionTerminationResponse,
} from "@/lib/api/model";
import { resetConversationsStore } from "@/features/conversations/store/conversations-store";
import { resetSearchRunsStore } from "@/stores/search-runs-store";

function logoutRequest(
  options: ExplicitLogoutOptions | undefined,
): LogoutRequest | null {
  return options?.returnTo ? { return_to: options.returnTo } : null;
}

export function useLogout() {
  const queryClient = useQueryClient();

  return useMutation<
    SessionTerminationResponse,
    Error,
    ExplicitLogoutOptions | undefined
  >({
    mutationFn: (options) => logout(logoutRequest(options)),
    onSuccess: (data, options) => {
      queryClient.clear();
      // Run phases / unseen markers are per-account; the next sign-in on
      // this tab must not inherit them.
      resetSearchRunsStore();
      // Meeting titles/state copies are per-account too.
      resetConversationsStore();
      handleExplicitLogout(data.logout_url, options);
    },
    onError: () => {
      showToast.error("Sign out failed", {
        description: "Your session is still active. Please try again.",
      });
    },
  });
}
